/**
 * AutoInsertQuizzesModal — Drop 2 (May 2026).
 *
 * Section-by-section planner: every content section is one row with an
 * AI/Manual/Skip action. AI rows get a per-row question count + language.
 * Presets are quick-set buttons that fan out across all rows (rows remain
 * individually editable afterwards). Bottom strip shows live counts.
 * Generate is blocked when the AI subset > 25.
 *
 * Backend contract: POST /modules/{id}/auto-quizzes/plan
 *   { rows:[{section_id, action, questions_count?, language?}], tone,
 *     run_self_audit, include_terminal_assessment, terminal_questions_count,
 *     language }
 */
import { useMemo, useState, useEffect } from 'react';
import { useApi } from '../../hooks/useApi';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import {
  Sparkles, Loader2, Edit3, MinusCircle, Wand2, AlertTriangle,
} from 'lucide-react';
import { toast } from 'sonner';

const ACTION_META = {
  ai:     { label: 'AI',     Icon: Sparkles,    cls: 'border-[var(--lumyn-content)] bg-[var(--lumyn-content)]/10 text-[var(--lumyn-content)]' },
  manual: { label: 'Manual', Icon: Edit3,       cls: 'border-amber-500 bg-amber-50 text-amber-700' },
  skip:   { label: 'Skip',   Icon: MinusCircle, cls: 'border-muted-foreground/30 bg-muted/40 text-muted-foreground' },
};

const AI_CAP = 25;
const COST_WARN_AI_THRESHOLD = 15;  // Drop 3 — soft warning before AI cap is reached

export default function AutoInsertQuizzesModal({ open, onClose, moduleId, sections = [], onDone }) {
  const { post } = useApi();
  const [rows, setRows] = useState([]);
  const [language, setLanguage] = useState('en');
  const [tone, setTone] = useState('formal');
  const [runSelfAudit, setRunSelfAudit] = useState(false);
  const [includeTerminal, setIncludeTerminal] = useState(false);
  const [terminalCount, setTerminalCount] = useState(5);
  const [mcqPct, setMcqPct] = useState(80);  // % MCQ; T/F is 100-mcqPct
  const [tfTone, setTfTone] = useState('neutral'); // 'neutral' | 'trap' — only affects T/F phrasing
  const [submitting, setSubmitting] = useState(false);

  const contentSections = useMemo(
    () => (sections || []).filter(s => s.section_type === 'content'),
    [sections]
  );

  // Seed rows whenever the modal opens or the section list changes.
  useEffect(() => {
    if (!open) return;
    setRows(contentSections.map(s => ({
      section_id: s.section_id,
      title: s.title,
      action: 'ai',
      questions_count: 4,
    })));
  }, [open, contentSections]);

  const setRow = (sid, patch) =>
    setRows(prev => prev.map(r => r.section_id === sid ? { ...r, ...patch } : r));

  const applyPreset = (kind) => {
    setRows(prev => prev.map((r, i) => {
      if (kind === 'all-ai')      return { ...r, action: 'ai',     questions_count: r.questions_count || 4 };
      if (kind === 'all-manual')  return { ...r, action: 'manual' };
      if (kind === 'all-skip')    return { ...r, action: 'skip' };
      if (kind === 'every-other') return { ...r, action: i % 2 === 0 ? 'ai' : 'skip' };
      return r;
    }));
  };

  const aiRows = rows.filter(r => r.action === 'ai');
  const manualRows = rows.filter(r => r.action === 'manual');
  const skipRows = rows.filter(r => r.action === 'skip');

  const aiQuizCount = aiRows.length + (includeTerminal ? 1 : 0);
  const aiQuestionCount =
    aiRows.reduce((sum, r) => sum + (parseInt(r.questions_count, 10) || 0), 0)
    + (includeTerminal ? (parseInt(terminalCount, 10) || 0) : 0);

  const overCap = aiQuizCount > AI_CAP;
  const nothingToDo = aiRows.length === 0 && manualRows.length === 0 && !includeTerminal;

  const handleSubmit = async () => {
    if (overCap) {
      toast.error(`AI subset (${aiQuizCount}) exceeds cap of ${AI_CAP}. Switch some rows to Manual or Skip.`);
      return;
    }
    if (nothingToDo) {
      toast.error('Pick at least one section to attach a quiz to (AI or Manual).');
      return;
    }
    // Drop 3 — soft warning before submitting a large AI batch
    if (aiQuizCount > COST_WARN_AI_THRESHOLD) {
      toast.warning(
        `Heads up: ${aiQuizCount} AI quizzes (~${aiQuestionCount} questions) — this may take a minute.`,
        { duration: 5000 }
      );
    }
    setSubmitting(true);
    try {
      const body = {
        rows: rows.map(r => ({
          section_id: r.section_id,
          action: r.action,
          ...(r.action === 'ai' ? { questions_count: Math.max(1, parseInt(r.questions_count, 10) || 4) } : {}),
        })),
        language,
        tone,
        run_self_audit: runSelfAudit,
        include_terminal_assessment: includeTerminal,
        terminal_questions_count: parseInt(terminalCount, 10) || 5,
        mcq_pct: mcqPct,
        tf_pct: 100 - mcqPct,
        tf_tone: tfTone,
      };
      const res = await post(`/modules/${moduleId}/auto-quizzes/plan`, body);
      toast.success(
        `${res.ai_quizzes_created} AI + ${res.manual_quizzes_created} manual quizzes created `
        + `(${res.questions_created} questions, all pending review).`
      );
      onDone?.(res);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Plan failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && !submitting && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="auto-insert-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-[var(--lumyn-content)]" />
            Auto-insert quizzes — section planner
          </DialogTitle>
          <p className="text-xs text-muted-foreground">
            Per-section control of how each quiz is created. AI rows draft questions
            from this module's slide content; manual rows insert an empty quiz you'll
            author in the editor; skip leaves the section alone. All AI questions land
            in the trainer review queue first.
          </p>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Presets */}
          <div className="flex flex-wrap items-center gap-1.5" data-testid="preset-bar">
            <span className="text-[11px] text-muted-foreground uppercase tracking-wider mr-1">Quick set:</span>
            <Button variant="outline" size="sm" onClick={() => applyPreset('all-ai')} data-testid="preset-all-ai">All AI</Button>
            <Button variant="outline" size="sm" onClick={() => applyPreset('all-manual')} data-testid="preset-all-manual">All Manual</Button>
            <Button variant="outline" size="sm" onClick={() => applyPreset('every-other')} data-testid="preset-every-other">AI every other</Button>
            <Button variant="outline" size="sm" onClick={() => applyPreset('all-skip')} data-testid="preset-all-skip">All Skip</Button>
          </div>

          {/* Section rows */}
          <div className="border rounded-lg max-h-80 overflow-y-auto divide-y" data-testid="plan-rows">
            {rows.length === 0 ? (
              <p className="text-xs text-muted-foreground p-4">No content sections in this module.</p>
            ) : rows.map((r, i) => (
              <PlanRow
                key={r.section_id}
                row={r}
                idx={i + 1}
                onChange={(patch) => setRow(r.section_id, patch)}
              />
            ))}
          </div>

          {/* Shared options */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium">Language (default)</label>
              <select
                className="mt-1 w-full text-sm p-2 rounded-md border bg-background"
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
                data-testid="lang-select"
              >
                <option value="en">English</option>
                <option value="ar">Arabic</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-medium">Tone</label>
              <select
                className="mt-1 w-full text-sm p-2 rounded-md border bg-background"
                value={tone}
                onChange={(e) => setTone(e.target.value)}
                data-testid="tone-select"
              >
                <option value="formal">Formal</option>
                <option value="conversational">Conversational</option>
                <option value="scenario">Scenario-based</option>
              </select>
            </div>
          </div>

          {/* Question-type mix — MCQ % vs True/False % (Phase 4) */}
          <div className="space-y-2 text-sm">
            <div className="flex items-center justify-between">
              <label className="text-xs font-medium">Question-type mix</label>
              <span className="text-xs text-muted-foreground tabular-nums">
                MCQ <span className="font-semibold text-foreground">{mcqPct}%</span>
                {' · '}
                T/F <span className="font-semibold text-foreground">{100 - mcqPct}%</span>
              </span>
            </div>
            <input
              type="range"
              min={0}
              max={100}
              step={5}
              value={mcqPct}
              onChange={(e) => setMcqPct(Number(e.target.value))}
              className="w-full accent-[var(--lumyn-banks)]"
              data-testid="mcq-pct-slider"
            />
            <p className="text-[11px] text-muted-foreground">
              Slide left for more True/False; right for all multiple-choice. Phase 4 restricts AI to these two types.
            </p>
            {/* T/F phrasing tone — only relevant when we're producing any T/F items */}
            {mcqPct < 100 && (
              <div className="flex items-center gap-2 pt-1">
                <span className="text-[11px] text-muted-foreground">T/F phrasing:</span>
                <div className="inline-flex rounded-md border bg-background overflow-hidden text-[11px]">
                  <button
                    type="button"
                    onClick={() => setTfTone('neutral')}
                    className={`px-2.5 py-1 transition ${tfTone === 'neutral' ? 'bg-[var(--lumyn-banks)]/10 text-[var(--lumyn-banks)] font-semibold' : 'text-muted-foreground hover:bg-muted/50'}`}
                    data-testid="tf-tone-neutral"
                    title='Plain factual statements (e.g. "The escalation SLA is 24 hours")'
                  >
                    Neutral
                  </button>
                  <button
                    type="button"
                    onClick={() => setTfTone('trap')}
                    className={`px-2.5 py-1 border-l transition ${tfTone === 'trap' ? 'bg-[var(--lumyn-banks)]/10 text-[var(--lumyn-banks)] font-semibold' : 'text-muted-foreground hover:bg-muted/50'}`}
                    data-testid="tf-tone-trap"
                    title='Common-misconception phrasings (e.g. "Tier-2 always supersedes tier-1")'
                  >
                    Trap-style
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Toggles */}
          <div className="space-y-2 text-sm">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={runSelfAudit}
                onChange={(e) => setRunSelfAudit(e.target.checked)}
                data-testid="audit-toggle"
              />
              <span>Run self-audit on each AI question (slower; flags ambiguous items)</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={includeTerminal}
                onChange={(e) => setIncludeTerminal(e.target.checked)}
                data-testid="terminal-toggle"
              />
              <span>Include final Assessment at the end (issues a certificate on pass)</span>
            </label>
            {includeTerminal && (
              <div className="flex items-center gap-2 pl-6">
                <span className="text-xs text-muted-foreground">Questions:</span>
                <Input
                  type="number"
                  min="1"
                  max="50"
                  value={terminalCount}
                  onChange={(e) => setTerminalCount(e.target.value)}
                  className="w-20 h-8 text-sm"
                  data-testid="terminal-count"
                />
              </div>
            )}
          </div>

          {/* Live preview */}
          <div
            className={`rounded-lg border p-3 flex flex-wrap items-center gap-3 text-sm ${
              overCap ? 'border-red-300 bg-red-50/60' : 'bg-muted/40'
            }`}
            data-testid="live-preview"
          >
            <Badge className="bg-[var(--lumyn-content)]/10 text-[var(--lumyn-content)] border-[var(--lumyn-content)]/30" variant="outline">
              <Sparkles className="w-3 h-3 mr-1" /> AI: {aiQuizCount}
            </Badge>
            <Badge variant="outline" className="border-amber-300 text-amber-700">
              <Edit3 className="w-3 h-3 mr-1" /> Manual: {manualRows.length}
            </Badge>
            <Badge variant="outline" className="text-muted-foreground">
              <MinusCircle className="w-3 h-3 mr-1" /> Skip: {skipRows.length}
            </Badge>
            <span className="text-xs text-muted-foreground">
              {aiQuestionCount} AI questions
            </span>
            {overCap && (
              <span className="ml-auto inline-flex items-center gap-1 text-xs text-red-700" data-testid="cap-warning">
                <AlertTriangle className="w-3.5 h-3.5" />
                Exceeds {AI_CAP}-quiz AI cap
              </span>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={submitting} data-testid="cancel-btn">
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting || overCap || nothingToDo}
            data-testid="submit-btn"
          >
            {submitting && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
            <Wand2 className="w-4 h-4 mr-1" />
            Generate
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PlanRow({ row, idx, onChange }) {
  return (
    <div
      className="flex items-center gap-2 p-2.5 hover:bg-muted/30"
      data-testid={`plan-row-${row.section_id}`}
    >
      <span className="text-xs font-mono text-muted-foreground w-7 flex-shrink-0">{idx}.</span>
      <span className="flex-1 truncate text-sm" title={row.title}>{row.title}</span>

      {/* Action picker */}
      <div className="inline-flex rounded-md border overflow-hidden" role="radiogroup" aria-label="Action">
        {(['ai', 'manual', 'skip']).map(act => {
          const m = ACTION_META[act];
          const Icon = m.Icon;
          const active = row.action === act;
          return (
            <button
              key={act}
              type="button"
              onClick={() => onChange({ action: act })}
              role="radio"
              aria-checked={active}
              data-testid={`row-action-${row.section_id}-${act}`}
              className={`inline-flex items-center gap-1 px-2 py-1 text-xs transition ${
                active ? m.cls : 'text-muted-foreground hover:bg-muted/60'
              }`}
            >
              <Icon className="w-3 h-3" /> {m.label}
            </button>
          );
        })}
      </div>

      {/* Per-row question count — only for AI */}
      <div className="w-16 flex-shrink-0">
        {row.action === 'ai' ? (
          <Input
            type="number"
            min="1"
            max="20"
            value={row.questions_count}
            onChange={(e) => onChange({ questions_count: e.target.value })}
            className="h-8 text-xs"
            data-testid={`row-count-${row.section_id}`}
            aria-label="Questions"
          />
        ) : (
          <div className="h-8" /> // spacer to keep row height stable
        )}
      </div>
    </div>
  );
}
